import React, { useEffect, useState, useRef } from 'react';
import { MailIcon, MessageSquareIcon, LightbulbIcon, BugIcon, SendIcon, ImageIcon, AlertCircleIcon, CheckCircleIcon, InfoIcon, XIcon, HelpCircleIcon, FileTextIcon, Loader2Icon, Clock8Icon, CheckIcon, TicketIcon, ListIcon, ClipboardIcon, ChevronRightIcon, ChevronDownIcon, PlusIcon, SearchIcon, BellIcon, FilterIcon, ArrowUpRightIcon, UserIcon, RefreshCwIcon, PhoneIcon } from 'lucide-react';
import { useAuth } from '../../components/auth/AuthProvider';
import { collection, addDoc, getDocs, query, where, orderBy, doc, getDoc, setDoc, updateDoc, serverTimestamp, Timestamp, limit, startAfter } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase/firebaseConfig';
// Support ticket types
type TicketStatus = 'open' | 'in-progress' | 'resolved' | 'closed';
type TicketPriority = 'low' | 'medium' | 'high' | 'urgent';
type TicketCategory = 'general' | 'account' | 'billing' | 'technical' | 'product' | 'other';
interface SupportTicket {
  id: string;
  userId: string;
  subject: string;
  description: string;
  category: TicketCategory;
  status: TicketStatus;
  priority: TicketPriority;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  attachmentUrls?: string[];
  assignedTo?: string;
  responses?: TicketResponse[];
}
interface TicketResponse {
  id: string;
  ticketId: string;
  message: string;
  createdAt: Timestamp;
  isFromStaff: boolean;
  staffName?: string;
  attachmentUrls?: string[];
}
// FAQ type
interface FAQ {
  id: string;
  question: string;
  answer: string;
  category: string;
}
// Contact preferences type
interface ContactPreferences {
  preferredMethod: 'email' | 'phone' | 'chat';
  emailNotifications: boolean;
  smsNotifications: boolean;
  pushNotifications: boolean;
  marketingEmails: boolean;
  emailAddress?: string;
  phoneNumber?: string;
  timezone?: string;
}
export const SupportFeedbackSection = () => {
  const {
    user
  } = useAuth();
  // States for loading and error handling
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  // Contact support states
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailFormData, setEmailFormData] = useState({
    name: '',
    subject: '',
    message: ''
  });
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [isSubmittingEmail, setIsSubmittingEmail] = useState(false);
  // Feature request states
  const [featureFormData, setFeatureFormData] = useState({
    description: '',
    category: ''
  });
  const [featureSubmitted, setFeatureSubmitted] = useState(false);
  const [isSubmittingFeature, setIsSubmittingFeature] = useState(false);
  // Bug report states
  const [bugFormData, setBugFormData] = useState({
    page: '',
    description: ''
  });
  const [bugScreenshot, setBugScreenshot] = useState<File | null>(null);
  const [bugSubmitted, setBugSubmitted] = useState(false);
  const [isSubmittingBug, setIsSubmittingBug] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Support tickets states
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [showTicketHistory, setShowTicketHistory] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [ticketResponse, setTicketResponse] = useState('');
  const [isSubmittingResponse, setIsSubmittingResponse] = useState(false);
  const [isLoadingMoreTickets, setIsLoadingMoreTickets] = useState(false);
  const [hasMoreTickets, setHasMoreTickets] = useState(true);
  const [lastTicket, setLastTicket] = useState<any>(null);
  // FAQ states
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [showFaqs, setShowFaqs] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState<string | null>(null);
  const [faqSearchTerm, setFaqSearchTerm] = useState('');
  const [filteredFaqs, setFilteredFaqs] = useState<FAQ[]>([]);
  const [selectedFaqCategory, setSelectedFaqCategory] = useState<string>('all');
  // Contact preferences states
  const [contactPreferences, setContactPreferences] = useState<ContactPreferences>({
    preferredMethod: 'email',
    emailNotifications: true,
    smsNotifications: false,
    pushNotifications: true,
    marketingEmails: false
  });
  const [showContactPreferences, setShowContactPreferences] = useState(false);
  const [isSavingPreferences, setIsSavingPreferences] = useState(false);
  const [preferencesSuccess, setPreferencesSuccess] = useState(false);
  // Live chat states
  const [isChatAvailable, setIsChatAvailable] = useState(false);
  const [showChatTooltip, setShowChatTooltip] = useState(false);
  // New ticket form states
  const [showNewTicketForm, setShowNewTicketForm] = useState(false);
  const [newTicketData, setNewTicketData] = useState({
    subject: '',
    description: '',
    category: 'general' as TicketCategory,
    priority: 'medium' as TicketPriority
  });
  const [newTicketAttachment, setNewTicketAttachment] = useState<File | null>(null);
  const [isSubmittingTicket, setIsSubmittingTicket] = useState(false);
  const ticketFileInputRef = useRef<HTMLInputElement>(null);
  // Fetch user support data
  useEffect(() => {
    if (!user?.id) return;
    const fetchUserSupportData = async () => {
      setIsLoading(true);
      setError('');
      // Fetch contact preferences — silently fall back to defaults on failure
      try {
        const preferencesDocRef = doc(db, 'contactPreferences', user.id);
        const preferencesDoc = await getDoc(preferencesDocRef);
        if (preferencesDoc.exists()) {
          setContactPreferences(preferencesDoc.data() as ContactPreferences);
        } else {
          try {
            const defaultPreferences: ContactPreferences = {
              preferredMethod: 'email',
              emailNotifications: true,
              smsNotifications: false,
              pushNotifications: true,
              marketingEmails: false,
              emailAddress: user.email
            };
            await setDoc(preferencesDocRef, defaultPreferences);
            setContactPreferences(defaultPreferences);
          } catch (writeErr) {
            console.warn('Could not create default contact preferences:', writeErr);
          }
        }
      } catch (err) {
        console.warn('Could not load contact preferences, using defaults:', err);
      }
      // Fetch support tickets — silently ignore failures
      try {
        await fetchSupportTickets();
      } catch (err) {
        console.warn('Could not load support tickets:', err);
      }
      // Fetch FAQs — silently ignore failures (has hardcoded fallback)
      try {
        await fetchFAQs();
      } catch (err) {
        console.warn('Could not load FAQs:', err);
      }
      // Check if live chat is available (would connect to a real service in production)
      setIsChatAvailable(false);
      setIsLoading(false);
    };
    fetchUserSupportData();
  }, [user]);
  // Fetch support tickets
  const fetchSupportTickets = async () => {
    if (!user?.id) return;
    try {
      const ticketsQuery = query(collection(db, 'supportTickets'), where('userId', '==', user.id), orderBy('createdAt', 'desc'), limit(5));
      const ticketSnapshot = await getDocs(ticketsQuery);
      if (!ticketSnapshot.empty) {
        const ticketData: SupportTicket[] = [];
        ticketSnapshot.forEach(doc => {
          ticketData.push({
            id: doc.id,
            ...doc.data()
          } as SupportTicket);
        });
        setTickets(ticketData);
        setHasMoreTickets(ticketData.length === 5);
        setLastTicket(ticketSnapshot.docs[ticketSnapshot.docs.length - 1]);
      } else {
        setTickets([]);
        setHasMoreTickets(false);
      }
    } catch (err) {
      console.error('Error fetching support tickets:', err);
    }
  };
  // Load more tickets
  const loadMoreTickets = async () => {
    if (!user?.id || !lastTicket || isLoadingMoreTickets) return;
    setIsLoadingMoreTickets(true);
    try {
      const ticketsQuery = query(collection(db, 'supportTickets'), where('userId', '==', user.id), orderBy('createdAt', 'desc'), startAfter(lastTicket), limit(5));
      const ticketSnapshot = await getDocs(ticketsQuery);
      if (!ticketSnapshot.empty) {
        const moreTickets: SupportTicket[] = [];
        ticketSnapshot.forEach(doc => {
          moreTickets.push({
            id: doc.id,
            ...doc.data()
          } as SupportTicket);
        });
        setTickets([...tickets, ...moreTickets]);
        setHasMoreTickets(moreTickets.length === 5);
        setLastTicket(ticketSnapshot.docs[ticketSnapshot.docs.length - 1]);
      } else {
        setHasMoreTickets(false);
      }
    } catch (err) {
      console.error('Error fetching more support tickets:', err);
    } finally {
      setIsLoadingMoreTickets(false);
    }
  };
  // Fetch FAQs
  const fetchFAQs = async () => {
    try {
      const faqsQuery = query(collection(db, 'faqs'), orderBy('category'), limit(20));
      const faqSnapshot = await getDocs(faqsQuery);
      if (!faqSnapshot.empty) {
        const faqData: FAQ[] = [];
        faqSnapshot.forEach(doc => {
          faqData.push({
            id: doc.id,
            ...doc.data()
          } as FAQ);
        });
        setFaqs(faqData);
        setFilteredFaqs(faqData);
      } else {
        // If no FAQs in database, use some default ones
        const defaultFaqs: FAQ[] = [{
          id: '1',
          question: 'How do I reset my password?',
          answer: 'You can reset your password by going to the Security section in your Account Settings. If you\'re locked out, use the "Forgot Password" option on the login screen.',
          category: 'account'
        }, {
          id: '2',
          question: 'When will I receive payment for my sales?',
          answer: 'Payments are processed every 14 days for all sales that have cleared the refund period. You can view your payment schedule in the Payments & Payouts section.',
          category: 'billing'
        }, {
          id: '3',
          question: 'How do I upload a new product?',
          answer: 'To upload a new product, go to your Seller Dashboard and click on "Add New Product". Follow the prompts to complete your product listing.',
          category: 'product'
        }, {
          id: '4',
          question: 'What file formats do you support for uploads?',
          answer: 'We support various file formats including ZIP, PDF, PSD, AI, EPS, JPG, PNG, SVG, and more. The maximum file size is 2GB per upload.',
          category: 'technical'
        }, {
          id: '5',
          question: 'How do I contact support?',
          answer: 'You can contact support by creating a new support ticket in your Account Settings under Support & Feedback. For urgent matters, email support@yourdomain.com directly.',
          category: 'general'
        }];
        setFaqs(defaultFaqs);
        setFilteredFaqs(defaultFaqs);
      }
    } catch (err) {
      console.error('Error fetching FAQs:', err);
    }
  };
  // Filter FAQs based on search term and category
  useEffect(() => {
    let filtered = faqs;
    // Apply category filter
    if (selectedFaqCategory !== 'all') {
      filtered = filtered.filter(faq => faq.category === selectedFaqCategory);
    }
    // Apply search term filter
    if (faqSearchTerm.trim()) {
      const searchLower = faqSearchTerm.toLowerCase();
      filtered = filtered.filter(faq => faq.question.toLowerCase().includes(searchLower) || faq.answer.toLowerCase().includes(searchLower));
    }
    setFilteredFaqs(filtered);
  }, [faqSearchTerm, selectedFaqCategory, faqs]);
  // Handle email form input changes
  const handleEmailInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setEmailFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  // Handle feature form input changes
  const handleFeatureInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFeatureFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  // Handle bug form input changes
  const handleBugInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setBugFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  // Handle new ticket form input changes
  const handleNewTicketInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setNewTicketData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  // Handle contact preferences changes
  const handlePreferenceChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    if (type === 'checkbox') {
      const checkbox = e.target as HTMLInputElement;
      setContactPreferences(prev => ({
        ...prev,
        [name]: checkbox.checked
      }));
    } else {
      setContactPreferences(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  // Handle file input change
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setBugScreenshot(e.target.files[0]);
    }
  };
  // Handle ticket attachment change
  const handleTicketAttachmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setNewTicketAttachment(e.target.files[0]);
    }
  };
  // Upload file to Firebase Storage
  const uploadFile = async (file: File, path: string): Promise<string> => {
    if (!user?.id) throw new Error('User not authenticated');
    const fileRef = ref(storage, `${path}/${user.id}/${Date.now()}_${file.name}`);
    await uploadBytes(fileRef, file);
    const downloadUrl = await getDownloadURL(fileRef);
    return downloadUrl;
  };
  // Handle email form submission
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) return;
    setIsSubmittingEmail(true);
    try {
      // Create a support ticket from the email form
      const ticketData = {
        userId: user.id,
        subject: emailFormData.subject,
        description: emailFormData.message,
        category: 'general' as TicketCategory,
        status: 'open' as TicketStatus,
        priority: 'medium' as TicketPriority,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        userEmail: user.email,
        userName: emailFormData.name || user.name
      };
      await addDoc(collection(db, 'supportTickets'), ticketData);
      // Log the support contact
      await addDoc(collection(db, 'supportLogs'), {
        userId: user.id,
        type: 'email_support',
        createdAt: serverTimestamp(),
        details: {
          subject: emailFormData.subject
        }
      });
      setEmailSubmitted(true);
      // Refresh tickets after submission
      await fetchSupportTickets();
      // Reset form after a delay
      setTimeout(() => {
        setEmailFormData({
          name: '',
          subject: '',
          message: ''
        });
        setEmailSubmitted(false);
        setShowEmailForm(false);
      }, 3000);
    } catch (err) {
      console.error('Error submitting support email:', err);
      setError('Failed to send your message. Please try again.');
    } finally {
      setIsSubmittingEmail(false);
    }
  };
  // Handle feature request submission
  const handleFeatureSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) return;
    setIsSubmittingFeature(true);
    try {
      // Add feature request to Firestore
      const featureData = {
        userId: user.id,
        category: featureFormData.category || 'Other',
        description: featureFormData.description,
        status: 'under-review',
        createdAt: serverTimestamp(),
        userEmail: user.email,
        userName: user.name
      };
      await addDoc(collection(db, 'featureRequests'), featureData);
      // Log the feature request
      await addDoc(collection(db, 'supportLogs'), {
        userId: user.id,
        type: 'feature_request',
        createdAt: serverTimestamp(),
        details: {
          category: featureFormData.category
        }
      });
      setFeatureSubmitted(true);
      // Reset form after a delay
      setTimeout(() => {
        setFeatureFormData({
          description: '',
          category: ''
        });
        setFeatureSubmitted(false);
      }, 3000);
    } catch (err) {
      console.error('Error submitting feature request:', err);
      setError('Failed to submit your feature request. Please try again.');
    } finally {
      setIsSubmittingFeature(false);
    }
  };
  // Handle bug report submission
  const handleBugSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) return;
    setIsSubmittingBug(true);
    try {
      // Upload screenshot if provided
      let screenshotUrl = '';
      if (bugScreenshot) {
        screenshotUrl = await uploadFile(bugScreenshot, 'bug-reports');
      }
      // Add bug report to Firestore
      const bugData = {
        userId: user.id,
        page: bugFormData.page,
        description: bugFormData.description,
        screenshotUrl: screenshotUrl,
        status: 'new',
        createdAt: serverTimestamp(),
        userEmail: user.email,
        userName: user.name,
        browser: navigator.userAgent
      };
      await addDoc(collection(db, 'bugReports'), bugData);
      // Log the bug report
      await addDoc(collection(db, 'supportLogs'), {
        userId: user.id,
        type: 'bug_report',
        createdAt: serverTimestamp(),
        details: {
          page: bugFormData.page
        }
      });
      setBugSubmitted(true);
      // Reset form after a delay
      setTimeout(() => {
        setBugFormData({
          page: '',
          description: ''
        });
        setBugScreenshot(null);
        setBugSubmitted(false);
      }, 3000);
    } catch (err) {
      console.error('Error submitting bug report:', err);
      setError('Failed to submit your bug report. Please try again.');
    } finally {
      setIsSubmittingBug(false);
    }
  };
  // Handle new ticket submission
  const handleNewTicketSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) return;
    setIsSubmittingTicket(true);
    try {
      // Upload attachment if provided
      let attachmentUrl = '';
      if (newTicketAttachment) {
        attachmentUrl = await uploadFile(newTicketAttachment, 'ticket-attachments');
      }
      // Create new support ticket
      const ticketData = {
        userId: user.id,
        subject: newTicketData.subject,
        description: newTicketData.description,
        category: newTicketData.category,
        status: 'open' as TicketStatus,
        priority: newTicketData.priority,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        userEmail: user.email,
        userName: user.name,
        attachmentUrls: attachmentUrl ? [attachmentUrl] : []
      };
      await addDoc(collection(db, 'supportTickets'), ticketData);
      // Log the ticket creation
      await addDoc(collection(db, 'supportLogs'), {
        userId: user.id,
        type: 'ticket_created',
        createdAt: serverTimestamp(),
        details: {
          subject: newTicketData.subject,
          category: newTicketData.category,
          priority: newTicketData.priority
        }
      });
      // Reset form
      setNewTicketData({
        subject: '',
        description: '',
        category: 'general',
        priority: 'medium'
      });
      setNewTicketAttachment(null);
      // Refresh tickets and show history
      await fetchSupportTickets();
      setShowNewTicketForm(false);
      setShowTicketHistory(true);
    } catch (err) {
      console.error('Error creating support ticket:', err);
      setError('Failed to create your support ticket. Please try again.');
    } finally {
      setIsSubmittingTicket(false);
    }
  };
  // Handle ticket selection
  const handleTicketSelect = async (ticket: SupportTicket) => {
    setSelectedTicket(ticket);
    // Fetch ticket responses if they don't exist
    if (!ticket.responses) {
      try {
        const responsesQuery = query(collection(db, 'ticketResponses'), where('ticketId', '==', ticket.id), orderBy('createdAt', 'asc'));
        const responseSnapshot = await getDocs(responsesQuery);
        if (!responseSnapshot.empty) {
          const responses: TicketResponse[] = [];
          responseSnapshot.forEach(doc => {
            responses.push({
              id: doc.id,
              ...doc.data()
            } as TicketResponse);
          });
          // Update the selected ticket with responses
          setSelectedTicket({
            ...ticket,
            responses
          });
        }
      } catch (err) {
        console.error('Error fetching ticket responses:', err);
      }
    }
  };
  // Handle ticket response submission
  const handleResponseSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id || !selectedTicket || !ticketResponse.trim()) return;
    setIsSubmittingResponse(true);
    try {
      // Add response to Firestore
      const responseData = {
        ticketId: selectedTicket.id,
        message: ticketResponse,
        createdAt: serverTimestamp(),
        isFromStaff: false,
        userId: user.id,
        userName: user.name
      };
      const responseRef = await addDoc(collection(db, 'ticketResponses'), responseData);
      // Update ticket's updatedAt timestamp
      const ticketRef = doc(db, 'supportTickets', selectedTicket.id);
      await updateDoc(ticketRef, {
        updatedAt: serverTimestamp(),
        status: selectedTicket.status === 'closed' ? 'open' : selectedTicket.status
      });
      // Update UI with new response
      const newResponse: TicketResponse = {
        id: responseRef.id,
        ticketId: selectedTicket.id,
        message: ticketResponse,
        createdAt: Timestamp.now(),
        isFromStaff: false
      };
      const updatedTicket = {
        ...selectedTicket,
        updatedAt: Timestamp.now(),
        status: selectedTicket.status === 'closed' ? 'open' as TicketStatus : selectedTicket.status,
        responses: selectedTicket.responses ? [...selectedTicket.responses, newResponse] : [newResponse]
      };
      setSelectedTicket(updatedTicket);
      // Update tickets list
      setTickets(tickets.map(t => t.id === selectedTicket.id ? updatedTicket : t));
      // Clear response input
      setTicketResponse('');
    } catch (err) {
      console.error('Error submitting ticket response:', err);
      setError('Failed to send your response. Please try again.');
    } finally {
      setIsSubmittingResponse(false);
    }
  };
  // Save contact preferences
  const saveContactPreferences = async () => {
    if (!user?.id) return;
    setIsSavingPreferences(true);
    setPreferencesSuccess(false);
    try {
      const preferencesRef = doc(db, 'contactPreferences', user.id);
      await setDoc(preferencesRef, {
        ...contactPreferences,
        updatedAt: serverTimestamp()
      });
      setPreferencesSuccess(true);
      // Clear success message after a delay
      setTimeout(() => {
        setPreferencesSuccess(false);
      }, 3000);
    } catch (err) {
      console.error('Error saving contact preferences:', err);
      setError('Failed to save your contact preferences. Please try again.');
    } finally {
      setIsSavingPreferences(false);
    }
  };
  // Trigger file input click
  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  // Trigger ticket attachment input click
  const handleTicketAttachmentClick = () => {
    if (ticketFileInputRef.current) {
      ticketFileInputRef.current.click();
    }
  };
  // Remove screenshot
  const handleRemoveScreenshot = () => {
    setBugScreenshot(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  // Remove ticket attachment
  const handleRemoveTicketAttachment = () => {
    setNewTicketAttachment(null);
    if (ticketFileInputRef.current) {
      ticketFileInputRef.current.value = '';
    }
  };
  // Get status badge color
  const getStatusColor = (status: TicketStatus) => {
    switch (status) {
      case 'open':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'in-progress':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'resolved':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'closed':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700/30 dark:text-gray-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700/30 dark:text-gray-300';
    }
  };
  // Get priority badge color
  const getPriorityColor = (priority: TicketPriority) => {
    switch (priority) {
      case 'low':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700/30 dark:text-gray-300';
      case 'medium':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'high':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300';
      case 'urgent':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700/30 dark:text-gray-300';
    }
  };
  // Format date
  const formatDate = (timestamp: Timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate();
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };
  if (isLoading) {
    return <div className="flex justify-center items-center min-h-[300px]" data-id="element-3736">
        <Loader2Icon className="h-8 w-8 text-[#2DB489] dark:text-[#4ED1A0] animate-spin" data-id="element-3737" />
        <span className="ml-2 text-gray-600 dark:text-[#B0C1BD]" data-id="element-3738">
          Loading support center...
        </span>
      </div>;
  }
  return <div data-id="element-3739">
      {/* Header - Mobile: single line, Desktop: flex with buttons on right */}
      <div className="mb-6" data-id="element-3740">
        <h2 className="text-2xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-4 md:mb-0 md:inline-block" data-id="element-3741">
          Support & Feedback
        </h2>

        {/* Sub-navigation - Mobile: horizontal row below header, Desktop: right-aligned */}
        <div className="flex flex-wrap gap-2 md:float-right md:mt-0" data-id="element-3742">
          <button onClick={() => {
          setShowTicketHistory(!showTicketHistory);
          setShowFaqs(false);
          setShowContactPreferences(false);
          setShowNewTicketForm(false);
          setSelectedTicket(null);
        }} className={`px-3 py-1.5 text-sm font-medium rounded-md flex items-center ${showTicketHistory ? 'bg-[#2DB489]/10 text-[#2DB489] dark:text-[#4ED1A0]' : 'text-gray-600 dark:text-[#B0C1BD] hover:bg-gray-100 dark:hover:bg-[#1A3330]/50'}`} data-id="element-3743">
            <ListIcon className="w-4 h-4 mr-1.5" data-id="element-3744" />
            My Tickets
          </button>
          <button onClick={() => {
          setShowFaqs(!showFaqs);
          setShowTicketHistory(false);
          setShowContactPreferences(false);
          setShowNewTicketForm(false);
          setSelectedTicket(null);
        }} className={`px-3 py-1.5 text-sm font-medium rounded-md flex items-center ${showFaqs ? 'bg-[#2DB489]/10 text-[#2DB489] dark:text-[#4ED1A0]' : 'text-gray-600 dark:text-[#B0C1BD] hover:bg-gray-100 dark:hover:bg-[#1A3330]/50'}`} data-id="element-3745">
            <HelpCircleIcon className="w-4 h-4 mr-1.5" data-id="element-3746" />
            FAQs
          </button>
          <button onClick={() => {
          setShowContactPreferences(!showContactPreferences);
          setShowTicketHistory(false);
          setShowFaqs(false);
          setShowNewTicketForm(false);
          setSelectedTicket(null);
        }} className={`px-3 py-1.5 text-sm font-medium rounded-md flex items-center ${showContactPreferences ? 'bg-[#2DB489]/10 text-[#2DB489] dark:text-[#4ED1A0]' : 'text-gray-600 dark:text-[#B0C1BD] hover:bg-gray-100 dark:hover:bg-[#1A3330]/50'}`} data-id="element-3747">
            <BellIcon className="w-4 h-4 mr-1.5" data-id="element-3748" />
            Preferences
          </button>
        </div>

        {/* Clear float on desktop */}
        <div className="clear-both" data-id="element-3749"></div>
      </div>
      {error && <div className="mb-6 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-md flex items-start" data-id="element-3750">
          <AlertCircleIcon className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" data-id="element-3751" />
          <p data-id="element-3752">{error}</p>
        </div>}
      {/* Support Ticket History */}
      {showTicketHistory && <section className="border border-gray-200 dark:border-[#1A3330] rounded-lg overflow-hidden mb-8" data-id="element-3753">
          <div className="bg-gray-50 dark:bg-[#1A3330]/30 px-5 py-4 border-b border-gray-200 dark:border-[#1A3330] flex justify-between items-center" data-id="element-3754">
            <div className="flex items-center" data-id="element-3755">
              <TicketIcon className="w-5 h-5 text-gray-500 dark:text-[#7A8F8A] mr-2" data-id="element-3756" />
              <h3 className="text-lg font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-3757">
                My Support Tickets
              </h3>
            </div>
            <button onClick={() => {
          setShowNewTicketForm(true);
          setSelectedTicket(null);
        }} className="flex items-center text-sm font-medium text-[#2DB489] dark:text-[#4ED1A0] hover:underline" data-id="element-3758">
              <PlusIcon className="w-4 h-4 mr-1" data-id="element-3759" />
              New Ticket
            </button>
          </div>
          <div className="p-5" data-id="element-3760">
            {showNewTicketForm ? <div className="bg-white dark:bg-[#1A3330]/30 border border-gray-200 dark:border-[#1A3330] rounded-lg p-4 mb-6" data-id="element-3761">
                <div className="flex justify-between items-center mb-4" data-id="element-3762">
                  <h4 className="text-lg font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-3763">
                    Create New Support Ticket
                  </h4>
                  <button onClick={() => setShowNewTicketForm(false)} className="text-gray-400 dark:text-[#7A8F8A] hover:text-gray-500 dark:hover:text-[#B0C1BD]" data-id="element-3764">
                    <XIcon className="w-5 h-5" data-id="element-3765" />
                  </button>
                </div>
                <form onSubmit={handleNewTicketSubmit} data-id="element-3766">
                  <div className="space-y-4" data-id="element-3767">
                    <div data-id="element-3768">
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-1" data-id="element-3769">
                        Subject <span className="text-red-500" data-id="element-3770">*</span>
                      </label>
                      <input type="text" id="subject" name="subject" value={newTicketData.subject} onChange={handleNewTicketInputChange} required className="w-full px-3 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent bg-white dark:bg-[#162825] text-gray-900 dark:text-[#F8FAF9]" placeholder="Brief description of your issue" data-id="element-3771" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3772">
                      <div data-id="element-3773">
                        <label htmlFor="category" className="block text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-1" data-id="element-3774">
                          Category
                        </label>
                        <select id="category" name="category" value={newTicketData.category} onChange={handleNewTicketInputChange} className="w-full px-3 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent bg-white dark:bg-[#162825] text-gray-900 dark:text-[#F8FAF9]" data-id="element-3775">
                          <option value="general" data-id="element-3776">General</option>
                          <option value="account" data-id="element-3777">Account</option>
                          <option value="billing" data-id="element-3778">Billing</option>
                          <option value="technical" data-id="element-3779">Technical</option>
                          <option value="product" data-id="element-3780">Product</option>
                          <option value="other" data-id="element-3781">Other</option>
                        </select>
                      </div>
                      <div data-id="element-3782">
                        <label htmlFor="priority" className="block text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-1" data-id="element-3783">
                          Priority
                        </label>
                        <select id="priority" name="priority" value={newTicketData.priority} onChange={handleNewTicketInputChange} className="w-full px-3 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent bg-white dark:bg-[#162825] text-gray-900 dark:text-[#F8FAF9]" data-id="element-3784">
                          <option value="low" data-id="element-3785">Low</option>
                          <option value="medium" data-id="element-3786">Medium</option>
                          <option value="high" data-id="element-3787">High</option>
                          <option value="urgent" data-id="element-3788">Urgent</option>
                        </select>
                      </div>
                    </div>
                    <div data-id="element-3789">
                      <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-1" data-id="element-3790">
                        Description <span className="text-red-500" data-id="element-3791">*</span>
                      </label>
                      <textarea id="description" name="description" value={newTicketData.description} onChange={handleNewTicketInputChange} required rows={4} className="w-full px-3 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent bg-white dark:bg-[#162825] text-gray-900 dark:text-[#F8FAF9]" placeholder="Please provide details about your issue..." data-id="element-3792" />
                    </div>
                    <div data-id="element-3793">
                      <label className="block text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-1" data-id="element-3794">
                        Attachment (Optional)
                      </label>
                      <input type="file" ref={ticketFileInputRef} onChange={handleTicketAttachmentChange} className="hidden" data-id="element-3795" />
                      {newTicketAttachment ? <div className="mt-2 relative" data-id="element-3796">
                          <div className="flex items-center p-3 bg-gray-50 dark:bg-[#1A3330] rounded-md border border-gray-200 dark:border-[#162825]" data-id="element-3797">
                            <div className="flex-shrink-0 mr-3" data-id="element-3798">
                              <ImageIcon className="w-5 h-5 text-gray-500 dark:text-[#7A8F8A]" data-id="element-3799" />
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-3800">
                              <p className="text-sm font-medium text-gray-900 dark:text-[#F8FAF9] truncate" data-id="element-3801">
                                {newTicketAttachment.name}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-[#7A8F8A]" data-id="element-3802">
                                {(newTicketAttachment.size / 1024).toFixed(1)}{' '}
                                KB
                              </p>
                            </div>
                            <button type="button" onClick={handleRemoveTicketAttachment} className="ml-3 flex-shrink-0 text-gray-400 dark:text-[#7A8F8A] hover:text-gray-500 dark:hover:text-[#B0C1BD]" data-id="element-3803">
                              <XIcon className="w-4 h-4" data-id="element-3804" />
                            </button>
                          </div>
                        </div> : <button type="button" onClick={handleTicketAttachmentClick} className="mt-1 px-4 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md text-sm font-medium text-gray-700 dark:text-[#B0C1BD] hover:bg-gray-50 dark:hover:bg-[#1A3330] transition-colors flex items-center" data-id="element-3805">
                          <ImageIcon className="w-4 h-4 mr-2" data-id="element-3806" />
                          Attach File
                        </button>}
                    </div>
                    <div className="flex justify-end" data-id="element-3807">
                      <button type="button" onClick={() => setShowNewTicketForm(false)} className="px-4 py-2 border border-gray-300 dark:border-[#1A3330] text-gray-700 dark:text-[#B0C1BD] rounded-md mr-3 hover:bg-gray-50 dark:hover:bg-[#1A3330]" data-id="element-3808">
                        Cancel
                      </button>
                      <button type="submit" disabled={isSubmittingTicket} className="px-4 py-2 bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-white font-medium rounded-md hover:shadow-md transition-all duration-200 flex items-center" data-id="element-3809">
                        {isSubmittingTicket ? <>
                            <Loader2Icon className="w-4 h-4 mr-2 animate-spin" data-id="element-3810" />
                            Submitting...
                          </> : <>
                            <SendIcon className="w-4 h-4 mr-2" data-id="element-3811" />
                            Submit Ticket
                          </>}
                      </button>
                    </div>
                  </div>
                </form>
              </div> : selectedTicket ? <div data-id="element-3812">
                <div className="mb-4" data-id="element-3813">
                  <button onClick={() => setSelectedTicket(null)} className="text-sm text-[#2DB489] dark:text-[#4ED1A0] font-medium flex items-center hover:underline" data-id="element-3814">
                    <ChevronDownIcon className="w-4 h-4 mr-1 rotate-90" data-id="element-3815" />
                    Back to Tickets
                  </button>
                </div>
                <div className="bg-white dark:bg-[#1A3330]/30 border border-gray-200 dark:border-[#1A3330] rounded-lg p-4 mb-4" data-id="element-3816">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4" data-id="element-3817">
                    <div data-id="element-3818">
                      <h4 className="text-lg font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-3819">
                        {selectedTicket.subject}
                      </h4>
                      <div className="flex flex-wrap gap-2 mt-2" data-id="element-3820">
                        <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full ${getStatusColor(selectedTicket.status)}`} data-id="element-3821">
                          {selectedTicket.status.charAt(0).toUpperCase() + selectedTicket.status.slice(1)}
                        </span>
                        <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full ${getPriorityColor(selectedTicket.priority)}`} data-id="element-3822">
                          {selectedTicket.priority.charAt(0).toUpperCase() + selectedTicket.priority.slice(1)}{' '}
                          Priority
                        </span>
                        <span className="px-2.5 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700/30 dark:text-gray-300" data-id="element-3823">
                          {selectedTicket.category.charAt(0).toUpperCase() + selectedTicket.category.slice(1)}
                        </span>
                      </div>
                    </div>
                    <div className="text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-3824">
                      <div className="flex items-center" data-id="element-3825">
                        <Clock8Icon className="w-4 h-4 mr-1" data-id="element-3826" />
                        <span data-id="element-3827">
                          Created: {formatDate(selectedTicket.createdAt)}
                        </span>
                      </div>
                      <div className="flex items-center mt-1" data-id="element-3828">
                        <RefreshCwIcon className="w-4 h-4 mr-1" data-id="element-3829" />
                        <span data-id="element-3830">
                          Updated: {formatDate(selectedTicket.updatedAt)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="p-3 bg-gray-50 dark:bg-[#162825] rounded-md mb-4" data-id="element-3831">
                    <p className="text-gray-700 dark:text-[#B0C1BD] whitespace-pre-line" data-id="element-3832">
                      {selectedTicket.description}
                    </p>
                    {selectedTicket.attachmentUrls && selectedTicket.attachmentUrls.length > 0 && <div className="mt-3 border-t border-gray-200 dark:border-[#1A3330] pt-3" data-id="element-3833">
                          <p className="text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-2" data-id="element-3834">
                            Attachments:
                          </p>
                          <div className="flex flex-wrap gap-2" data-id="element-3835">
                            {selectedTicket.attachmentUrls.map((url, index) => <a key={index} href={url} target="_blank" rel="noopener noreferrer" className="flex items-center px-3 py-1.5 bg-white dark:bg-[#1A3330] border border-gray-200 dark:border-[#162825] rounded text-sm text-[#2DB489] dark:text-[#4ED1A0] hover:underline" data-id="element-3836">
                                <ImageIcon className="w-4 h-4 mr-1.5" data-id="element-3837" />
                                View Attachment {index + 1}
                                <ArrowUpRightIcon className="w-3 h-3 ml-1" data-id="element-3838" />
                              </a>)}
                          </div>
                        </div>}
                  </div>
                  {/* Ticket Responses */}
                  {selectedTicket.responses && selectedTicket.responses.length > 0 && <div className="mb-4" data-id="element-3839">
                        <h5 className="text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-3" data-id="element-3840">
                          Conversation History:
                        </h5>
                        <div className="space-y-4" data-id="element-3841">
                          {selectedTicket.responses.map(response => <div key={response.id} className={`p-3 rounded-lg ${response.isFromStaff ? 'bg-blue-50 dark:bg-blue-900/10 ml-4' : 'bg-gray-50 dark:bg-[#162825] mr-4'}`} data-id="element-3842">
                              <div className="flex justify-between items-start mb-2" data-id="element-3843">
                                <div className="flex items-center" data-id="element-3844">
                                  <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-2 ${response.isFromStaff ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300' : 'bg-gray-100 dark:bg-[#1A3330] text-gray-700 dark:text-[#B0C1BD]'}`} data-id="element-3845">
                                    {response.isFromStaff ? <UserIcon className="w-3.5 h-3.5" data-id="element-3846" /> : <UserIcon className="w-3.5 h-3.5" data-id="element-3847" />}
                                  </div>
                                  <span className="text-sm font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-3848">
                                    {response.isFromStaff ? response.staffName || 'Support Agent' : 'You'}
                                  </span>
                                </div>
                                <span className="text-xs text-gray-500 dark:text-[#7A8F8A]" data-id="element-3849">
                                  {formatDate(response.createdAt)}
                                </span>
                              </div>
                              <p className="text-gray-700 dark:text-[#B0C1BD] whitespace-pre-line" data-id="element-3850">
                                {response.message}
                              </p>
                              {response.attachmentUrls && response.attachmentUrls.length > 0 && <div className="mt-2" data-id="element-3851">
                                    <div className="flex flex-wrap gap-2" data-id="element-3852">
                                      {response.attachmentUrls.map((url, index) => <a key={index} href={url} target="_blank" rel="noopener noreferrer" className="flex items-center px-2 py-1 bg-white dark:bg-[#1A3330] border border-gray-200 dark:border-[#162825] rounded text-xs text-[#2DB489] dark:text-[#4ED1A0] hover:underline" data-id="element-3853">
                                            <ImageIcon className="w-3 h-3 mr-1" data-id="element-3854" />
                                            Attachment {index + 1}
                                            <ArrowUpRightIcon className="w-2 h-2 ml-1" data-id="element-3855" />
                                          </a>)}
                                    </div>
                                  </div>}
                            </div>)}
                        </div>
                      </div>}
                  {/* Reply Form */}
                  <div className="border-t border-gray-200 dark:border-[#1A3330] pt-4" data-id="element-3856">
                    <h5 className="text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-2" data-id="element-3857">
                      Add a reply:
                    </h5>
                    <form onSubmit={handleResponseSubmit} data-id="element-3858">
                      <textarea value={ticketResponse} onChange={e => setTicketResponse(e.target.value)} rows={3} className="w-full px-3 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent bg-white dark:bg-[#162825] text-gray-900 dark:text-[#F8FAF9]" placeholder="Type your reply here..." required data-id="element-3859" />
                      <div className="flex justify-end mt-3" data-id="element-3860">
                        <button type="submit" disabled={isSubmittingResponse || !ticketResponse.trim()} className="px-4 py-2 bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-white font-medium rounded-md hover:shadow-md transition-all duration-200 flex items-center disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-3861">
                          {isSubmittingResponse ? <>
                              <Loader2Icon className="w-4 h-4 mr-2 animate-spin" data-id="element-3862" />
                              Sending...
                            </> : <>
                              <SendIcon className="w-4 h-4 mr-2" data-id="element-3863" />
                              Send Reply
                            </>}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div> : <>
                {tickets.length === 0 ? <div className="text-center py-8" data-id="element-3864">
                    <div className="w-16 h-16 bg-gray-100 dark:bg-[#1A3330] rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-3865">
                      <TicketIcon className="w-8 h-8 text-gray-400 dark:text-[#7A8F8A]" data-id="element-3866" />
                    </div>
                    <h4 className="text-lg font-medium text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-3867">
                      No Support Tickets
                    </h4>
                    <p className="text-gray-500 dark:text-[#7A8F8A] mb-4" data-id="element-3868">
                      You haven't created any support tickets yet.
                    </p>
                    <button onClick={() => setShowNewTicketForm(true)} className="px-4 py-2 bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-white font-medium rounded-md hover:shadow-md transition-all duration-200 inline-flex items-center" data-id="element-3869">
                      <PlusIcon className="w-4 h-4 mr-2" data-id="element-3870" />
                      Create Your First Ticket
                    </button>
                  </div> : <div data-id="element-3871">
                    <div className="overflow-x-auto" data-id="element-3872">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-[#1A3330]" data-id="element-3873">
                        <thead className="bg-gray-50 dark:bg-[#1A3330]/30" data-id="element-3874">
                          <tr data-id="element-3875">
                            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#7A8F8A] uppercase tracking-wider" data-id="element-3876">
                              Subject
                            </th>
                            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#7A8F8A] uppercase tracking-wider" data-id="element-3877">
                              Status
                            </th>
                            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#7A8F8A] uppercase tracking-wider" data-id="element-3878">
                              Category
                            </th>
                            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#7A8F8A] uppercase tracking-wider" data-id="element-3879">
                              Last Update
                            </th>
                            <th scope="col" className="relative px-4 py-3" data-id="element-3880">
                              <span className="sr-only" data-id="element-3881">View</span>
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-[#162825] divide-y divide-gray-200 dark:divide-[#1A3330]" data-id="element-3882">
                          {tickets.map(ticket => <tr key={ticket.id} className="hover:bg-gray-50 dark:hover:bg-[#1A3330]/30 cursor-pointer" onClick={() => handleTicketSelect(ticket)} data-id="element-3883">
                              <td className="px-4 py-4 whitespace-nowrap" data-id="element-3884">
                                <div className="text-sm font-medium text-gray-900 dark:text-[#F8FAF9]" data-id="element-3885">
                                  {ticket.subject}
                                </div>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap" data-id="element-3886">
                                <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full ${getStatusColor(ticket.status)}`} data-id="element-3887">
                                  {ticket.status.charAt(0).toUpperCase() + ticket.status.slice(1)}
                                </span>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap" data-id="element-3888">
                                <div className="text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-3889">
                                  {ticket.category.charAt(0).toUpperCase() + ticket.category.slice(1)}
                                </div>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-3890">
                                {formatDate(ticket.updatedAt)}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-3891">
                                <button onClick={e => {
                        e.stopPropagation();
                        handleTicketSelect(ticket);
                      }} className="text-[#2DB489] dark:text-[#4ED1A0] hover:underline" data-id="element-3892">
                                  View
                                </button>
                              </td>
                            </tr>)}
                        </tbody>
                      </table>
                    </div>
                    {hasMoreTickets && <div className="mt-4 text-center" data-id="element-3893">
                        <button onClick={loadMoreTickets} disabled={isLoadingMoreTickets} className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-[#B0C1BD] bg-white dark:bg-[#162825] hover:bg-gray-50 dark:hover:bg-[#1A3330] focus:outline-none" data-id="element-3894">
                          {isLoadingMoreTickets ? <>
                              <Loader2Icon className="w-4 h-4 mr-2 animate-spin" data-id="element-3895" />
                              Loading...
                            </> : 'Load More Tickets'}
                        </button>
                      </div>}
                  </div>}
              </>}
          </div>
        </section>}
      {/* FAQs Section */}
      {showFaqs && <section className="border border-gray-200 dark:border-[#1A3330] rounded-lg overflow-hidden mb-8" data-id="element-3896">
          <div className="bg-gray-50 dark:bg-[#1A3330]/30 px-5 py-4 border-b border-gray-200 dark:border-[#1A3330]" data-id="element-3897">
            <div className="flex items-center" data-id="element-3898">
              <HelpCircleIcon className="w-5 h-5 text-gray-500 dark:text-[#7A8F8A] mr-2" data-id="element-3899" />
              <h3 className="text-lg font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-3900">
                Frequently Asked Questions
              </h3>
            </div>
          </div>
          <div className="p-5" data-id="element-3901">
            <div className="mb-6 flex flex-col sm:flex-row gap-4" data-id="element-3902">
              <div className="relative flex-1" data-id="element-3903">
                <input type="text" placeholder="Search FAQs..." value={faqSearchTerm} onChange={e => setFaqSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent bg-white dark:bg-[#162825] text-gray-900 dark:text-[#F8FAF9]" data-id="element-3904" />
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-[#7A8F8A]" data-id="element-3905" />
              </div>
              <select value={selectedFaqCategory} onChange={e => setSelectedFaqCategory(e.target.value)} className="px-3 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent bg-white dark:bg-[#162825] text-gray-900 dark:text-[#F8FAF9]" data-id="element-3906">
                <option value="all" data-id="element-3907">All Categories</option>
                <option value="account" data-id="element-3908">Account</option>
                <option value="billing" data-id="element-3909">Billing</option>
                <option value="product" data-id="element-3910">Product</option>
                <option value="technical" data-id="element-3911">Technical</option>
                <option value="general" data-id="element-3912">General</option>
              </select>
            </div>
            {filteredFaqs.length === 0 ? <div className="text-center py-8" data-id="element-3913">
                <div className="w-16 h-16 bg-gray-100 dark:bg-[#1A3330] rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-3914">
                  <SearchIcon className="w-8 h-8 text-gray-400 dark:text-[#7A8F8A]" data-id="element-3915" />
                </div>
                <h4 className="text-lg font-medium text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-3916">
                  No results found
                </h4>
                <p className="text-gray-500 dark:text-[#7A8F8A]" data-id="element-3917">
                  Try adjusting your search or filter to find what you're
                  looking for.
                </p>
              </div> : <div className="space-y-4" data-id="element-3918">
                {filteredFaqs.map(faq => <div key={faq.id} className="border border-gray-200 dark:border-[#1A3330] rounded-lg overflow-hidden" data-id="element-3919">
                    <button onClick={() => setExpandedFaq(expandedFaq === faq.id ? null : faq.id)} className="w-full px-4 py-3 flex justify-between items-center bg-white dark:bg-[#162825] hover:bg-gray-50 dark:hover:bg-[#1A3330]/30 transition-colors text-left" data-id="element-3920">
                      <div className="flex items-center" data-id="element-3921">
                        <span className="font-medium text-gray-900 dark:text-[#F8FAF9]" data-id="element-3922">
                          {faq.question}
                        </span>
                        <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-800 dark:bg-[#1A3330] dark:text-[#B0C1BD]" data-id="element-3923">
                          {faq.category}
                        </span>
                      </div>
                      <ChevronDownIcon className={`w-5 h-5 text-gray-400 dark:text-[#7A8F8A] transition-transform ${expandedFaq === faq.id ? 'transform rotate-180' : ''}`} data-id="element-3924" />
                    </button>
                    {expandedFaq === faq.id && <div className="px-4 py-3 bg-gray-50 dark:bg-[#1A3330]/30 border-t border-gray-200 dark:border-[#1A3330]" data-id="element-3925">
                        <p className="text-gray-700 dark:text-[#B0C1BD]" data-id="element-3926">
                          {faq.answer}
                        </p>
                      </div>}
                  </div>)}
              </div>}
            <div className="mt-6 text-center" data-id="element-3927">
              <p className="text-gray-500 dark:text-[#7A8F8A] text-sm" data-id="element-3928">
                Can't find what you're looking for?{' '}
                <button onClick={() => {
              setShowFaqs(false);
              setShowNewTicketForm(true);
              setShowTicketHistory(true);
            }} className="text-[#2DB489] dark:text-[#4ED1A0] hover:underline font-medium" data-id="element-3929">
                  Contact Support
                </button>
              </p>
            </div>
          </div>
        </section>}
      {/* Contact Preferences Section */}
      {showContactPreferences && <section className="border border-gray-200 dark:border-[#1A3330] rounded-lg overflow-hidden mb-8" data-id="element-3930">
          <div className="bg-gray-50 dark:bg-[#1A3330]/30 px-5 py-4 border-b border-gray-200 dark:border-[#1A3330]" data-id="element-3931">
            <div className="flex items-center" data-id="element-3932">
              <BellIcon className="w-5 h-5 text-gray-500 dark:text-[#7A8F8A] mr-2" data-id="element-3933" />
              <h3 className="text-lg font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-3934">
                Contact Preferences
              </h3>
            </div>
          </div>
          <div className="p-5" data-id="element-3935">
            {preferencesSuccess && <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-md flex items-start" data-id="element-3936">
                <CheckCircleIcon className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" data-id="element-3937" />
                <p data-id="element-3938">Your contact preferences have been updated successfully.</p>
              </div>}
            <div className="space-y-6" data-id="element-3939">
              <div data-id="element-3940">
                <h4 className="text-base font-medium text-gray-800 dark:text-[#F8FAF9] mb-3" data-id="element-3941">
                  Preferred Contact Method
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-3942">
                  <label className={`flex items-center p-4 border rounded-lg cursor-pointer transition-colors ${contactPreferences.preferredMethod === 'email' ? 'border-[#2DB489] dark:border-[#4ED1A0] bg-[#2DB489]/5 dark:bg-[#4ED1A0]/5' : 'border-gray-200 dark:border-[#1A3330] bg-white dark:bg-[#162825] hover:bg-gray-50 dark:hover:bg-[#1A3330]/30'}`} data-id="element-3943">
                    <input type="radio" name="preferredMethod" value="email" checked={contactPreferences.preferredMethod === 'email'} onChange={handlePreferenceChange} className="sr-only" data-id="element-3944" />
                    <MailIcon className={`w-5 h-5 mr-3 ${contactPreferences.preferredMethod === 'email' ? 'text-[#2DB489] dark:text-[#4ED1A0]' : 'text-gray-400 dark:text-[#7A8F8A]'}`} data-id="element-3945" />
                    <div data-id="element-3946">
                      <span className={`font-medium ${contactPreferences.preferredMethod === 'email' ? 'text-[#2DB489] dark:text-[#4ED1A0]' : 'text-gray-700 dark:text-[#B0C1BD]'}`} data-id="element-3947">
                        Email
                      </span>
                    </div>
                  </label>
                  <label className={`flex items-center p-4 border rounded-lg cursor-pointer transition-colors ${contactPreferences.preferredMethod === 'phone' ? 'border-[#2DB489] dark:border-[#4ED1A0] bg-[#2DB489]/5 dark:bg-[#4ED1A0]/5' : 'border-gray-200 dark:border-[#1A3330] bg-white dark:bg-[#162825] hover:bg-gray-50 dark:hover:bg-[#1A3330]/30'}`} data-id="element-3948">
                    <input type="radio" name="preferredMethod" value="phone" checked={contactPreferences.preferredMethod === 'phone'} onChange={handlePreferenceChange} className="sr-only" data-id="element-3949" />
                    <PhoneIcon className={`w-5 h-5 mr-3 ${contactPreferences.preferredMethod === 'phone' ? 'text-[#2DB489] dark:text-[#4ED1A0]' : 'text-gray-400 dark:text-[#7A8F8A]'}`} data-id="element-3950" />
                    <div data-id="element-3951">
                      <span className={`font-medium ${contactPreferences.preferredMethod === 'phone' ? 'text-[#2DB489] dark:text-[#4ED1A0]' : 'text-gray-700 dark:text-[#B0C1BD]'}`} data-id="element-3952">
                        Phone
                      </span>
                    </div>
                  </label>
                  <label className={`flex items-center p-4 border rounded-lg cursor-pointer transition-colors ${contactPreferences.preferredMethod === 'chat' ? 'border-[#2DB489] dark:border-[#4ED1A0] bg-[#2DB489]/5 dark:bg-[#4ED1A0]/5' : 'border-gray-200 dark:border-[#1A3330] bg-white dark:bg-[#162825] hover:bg-gray-50 dark:hover:bg-[#1A3330]/30'}`} data-id="element-3953">
                    <input type="radio" name="preferredMethod" value="chat" checked={contactPreferences.preferredMethod === 'chat'} onChange={handlePreferenceChange} className="sr-only" data-id="element-3954" />
                    <MessageSquareIcon className={`w-5 h-5 mr-3 ${contactPreferences.preferredMethod === 'chat' ? 'text-[#2DB489] dark:text-[#4ED1A0]' : 'text-gray-400 dark:text-[#7A8F8A]'}`} data-id="element-3955" />
                    <div data-id="element-3956">
                      <span className={`font-medium ${contactPreferences.preferredMethod === 'chat' ? 'text-[#2DB489] dark:text-[#4ED1A0]' : 'text-gray-700 dark:text-[#B0C1BD]'}`} data-id="element-3957">
                        Live Chat
                      </span>
                    </div>
                  </label>
                </div>
              </div>
              <div data-id="element-3958">
                <h4 className="text-base font-medium text-gray-800 dark:text-[#F8FAF9] mb-3" data-id="element-3959">
                  Notification Preferences
                </h4>
                <div className="space-y-3" data-id="element-3960">
                  <div className="flex items-center justify-between" data-id="element-3961">
                    <div data-id="element-3962">
                      <h5 className="text-sm font-medium text-gray-700 dark:text-[#B0C1BD]" data-id="element-3963">
                        Email Notifications
                      </h5>
                      <p className="text-xs text-gray-500 dark:text-[#7A8F8A]" data-id="element-3964">
                        Receive updates about your support tickets via email
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer" data-id="element-3965">
                      <input type="checkbox" name="emailNotifications" className="sr-only peer" checked={contactPreferences.emailNotifications} onChange={handlePreferenceChange} data-id="element-3966" />
                      <div className="w-11 h-6 bg-gray-200 dark:bg-[#1A3330] rounded-full peer peer-focus:ring-2 peer-focus:ring-[#2DB489] dark:peer-focus:ring-[#4ED1A0] peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#2DB489] dark:peer-checked:bg-[#4ED1A0]" data-id="element-3967"></div>
                    </label>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-3968">
                    <div data-id="element-3969">
                      <h5 className="text-sm font-medium text-gray-700 dark:text-[#B0C1BD]" data-id="element-3970">
                        SMS Notifications
                      </h5>
                      <p className="text-xs text-gray-500 dark:text-[#7A8F8A]" data-id="element-3971">
                        Receive text message alerts for urgent updates
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer" data-id="element-3972">
                      <input type="checkbox" name="smsNotifications" className="sr-only peer" checked={contactPreferences.smsNotifications} onChange={handlePreferenceChange} data-id="element-3973" />
                      <div className="w-11 h-6 bg-gray-200 dark:bg-[#1A3330] rounded-full peer peer-focus:ring-2 peer-focus:ring-[#2DB489] dark:peer-focus:ring-[#4ED1A0] peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#2DB489] dark:peer-checked:bg-[#4ED1A0]" data-id="element-3974"></div>
                    </label>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-3975">
                    <div data-id="element-3976">
                      <h5 className="text-sm font-medium text-gray-700 dark:text-[#B0C1BD]" data-id="element-3977">
                        Push Notifications
                      </h5>
                      <p className="text-xs text-gray-500 dark:text-[#7A8F8A]" data-id="element-3978">
                        Receive browser notifications for ticket updates
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer" data-id="element-3979">
                      <input type="checkbox" name="pushNotifications" className="sr-only peer" checked={contactPreferences.pushNotifications} onChange={handlePreferenceChange} data-id="element-3980" />
                      <div className="w-11 h-6 bg-gray-200 dark:bg-[#1A3330] rounded-full peer peer-focus:ring-2 peer-focus:ring-[#2DB489] dark:peer-focus:ring-[#4ED1A0] peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#2DB489] dark:peer-checked:bg-[#4ED1A0]" data-id="element-3981"></div>
                    </label>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-3982">
                    <div data-id="element-3983">
                      <h5 className="text-sm font-medium text-gray-700 dark:text-[#B0C1BD]" data-id="element-3984">
                        Marketing Emails
                      </h5>
                      <p className="text-xs text-gray-500 dark:text-[#7A8F8A]" data-id="element-3985">
                        Receive updates about new features and promotions
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer" data-id="element-3986">
                      <input type="checkbox" name="marketingEmails" className="sr-only peer" checked={contactPreferences.marketingEmails} onChange={handlePreferenceChange} data-id="element-3987" />
                      <div className="w-11 h-6 bg-gray-200 dark:bg-[#1A3330] rounded-full peer peer-focus:ring-2 peer-focus:ring-[#2DB489] dark:peer-focus:ring-[#4ED1A0] peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#2DB489] dark:peer-checked:bg-[#4ED1A0]" data-id="element-3988"></div>
                    </label>
                  </div>
                </div>
              </div>
              <div className="flex justify-end" data-id="element-3989">
                <button onClick={saveContactPreferences} disabled={isSavingPreferences} className="px-4 py-2 bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-white font-medium rounded-lg hover:shadow-md transition-all duration-200 flex items-center" data-id="element-3990">
                  {isSavingPreferences ? <>
                      <Loader2Icon className="w-4 h-4 mr-2 animate-spin" data-id="element-3991" />
                      Saving...
                    </> : 'Save Preferences'}
                </button>
              </div>
            </div>
          </div>
        </section>}
      {/* Contact Support Section */}
      {!showTicketHistory && !showFaqs && !showContactPreferences && <section className="border border-gray-200 dark:border-[#1A3330] rounded-lg overflow-hidden" data-id="element-3992">
          <div className="bg-gray-50 dark:bg-[#1A3330]/30 px-5 py-4 border-b border-gray-200 dark:border-[#1A3330]" data-id="element-3993">
            <div className="flex items-center" data-id="element-3994">
              <HelpCircleIcon className="w-5 h-5 text-gray-500 dark:text-[#7A8F8A] mr-2" data-id="element-3995" />
              <h3 className="text-lg font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-3996">
                Contact Support
              </h3>
            </div>
          </div>
          <div className="p-5" data-id="element-3997">
            <p className="text-gray-600 dark:text-[#B0C1BD] mb-5" data-id="element-3998">
              Need help with your store, account, or product uploads? We're here
              to support you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4" data-id="element-3999">
              {/* Email Support Option */}
              <div className="flex-1" data-id="element-4000">
                {!showEmailForm ? <button onClick={() => setShowEmailForm(true)} className="w-full py-3 px-4 flex items-center justify-center bg-white dark:bg-[#1A3330] border border-gray-200 dark:border-[#162825] rounded-lg hover:bg-gray-50 dark:hover:bg-[#162825] transition-colors group" data-id="element-4001">
                    <MailIcon className="w-5 h-5 text-[#2DB489] dark:text-[#4ED1A0] mr-2" data-id="element-4002" />
                    <span className="font-medium text-gray-800 dark:text-[#F8FAF9] group-hover:text-[#2DB489] dark:group-hover:text-[#4ED1A0] transition-colors" data-id="element-4003">
                      Email Support
                    </span>
                  </button> : <div className="bg-white dark:bg-[#1A3330] border border-gray-200 dark:border-[#162825] rounded-lg p-4" data-id="element-4004">
                    <div className="flex justify-between items-center mb-3" data-id="element-4005">
                      <h4 className="text-base font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-4006">
                        Send us an email
                      </h4>
                      <button onClick={() => setShowEmailForm(false)} className="text-gray-400 dark:text-[#7A8F8A] hover:text-gray-500 dark:hover:text-[#B0C1BD]" data-id="element-4007">
                        <XIcon className="w-4 h-4" data-id="element-4008" />
                      </button>
                    </div>
                    {emailSubmitted ? <div className="p-3 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-md flex items-start" data-id="element-4009">
                        <CheckCircleIcon className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" data-id="element-4010" />
                        <p data-id="element-4011">
                          Your message has been sent! We'll get back to you
                          soon.
                        </p>
                      </div> : <form onSubmit={handleEmailSubmit} data-id="element-4012">
                        <div className="space-y-3" data-id="element-4013">
                          <div data-id="element-4014">
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-1" data-id="element-4015">
                              Your Name
                            </label>
                            <input type="text" id="name" name="name" value={emailFormData.name} onChange={handleEmailInputChange} required className="w-full px-3 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent bg-white dark:bg-[#162825] text-gray-900 dark:text-[#F8FAF9]" placeholder="John Smith" data-id="element-4016" />
                          </div>
                          <div data-id="element-4017">
                            <label htmlFor="subject" className="block text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-1" data-id="element-4018">
                              Subject
                            </label>
                            <input type="text" id="subject" name="subject" value={emailFormData.subject} onChange={handleEmailInputChange} required className="w-full px-3 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent bg-white dark:bg-[#162825] text-gray-900 dark:text-[#F8FAF9]" placeholder="Question about my account" data-id="element-4019" />
                          </div>
                          <div data-id="element-4020">
                            <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-1" data-id="element-4021">
                              Message
                            </label>
                            <textarea id="message" name="message" value={emailFormData.message} onChange={handleEmailInputChange} required rows={4} className="w-full px-3 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent bg-white dark:bg-[#162825] text-gray-900 dark:text-[#F8FAF9]" placeholder="Please describe your issue or question in detail..." data-id="element-4022" />
                          </div>
                          <div className="flex justify-end" data-id="element-4023">
                            <button type="submit" disabled={isSubmittingEmail} className="flex items-center px-4 py-2 bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-white font-medium rounded-lg hover:shadow-md transition-all duration-200" data-id="element-4024">
                              {isSubmittingEmail ? <>
                                  <Loader2Icon className="w-4 h-4 mr-2 animate-spin" data-id="element-4025" />
                                  Sending...
                                </> : <>
                                  <SendIcon className="w-4 h-4 mr-2" data-id="element-4026" />
                                  Send Message
                                </>}
                            </button>
                          </div>
                        </div>
                      </form>}
                    <div className="mt-3 text-sm text-gray-500 dark:text-[#7A8F8A] flex items-center" data-id="element-4027">
                      <InfoIcon className="w-4 h-4 mr-1" data-id="element-4028" />
                      <span data-id="element-4029">
                        Or email us directly at{' '}
                        <a href="mailto:support@yourdomain.com" className="text-[#2DB489] dark:text-[#4ED1A0] hover:underline" data-id="element-4030">
                          support@yourdomain.com
                        </a>
                      </span>
                    </div>
                  </div>}
              </div>
              {/* Live Chat Option */}
              <div className="flex-1 relative" data-id="element-4031">
                <button onMouseEnter={() => setShowChatTooltip(true)} onMouseLeave={() => setShowChatTooltip(false)} className="w-full py-3 px-4 flex items-center justify-center bg-white dark:bg-[#1A3330] border border-gray-200 dark:border-[#162825] rounded-lg hover:bg-gray-50 dark:hover:bg-[#162825] transition-colors group cursor-not-allowed opacity-80" data-id="element-4032">
                  <MessageSquareIcon className="w-5 h-5 text-gray-400 dark:text-[#7A8F8A] mr-2" data-id="element-4033" />
                  <span className="font-medium text-gray-400 dark:text-[#7A8F8A]" data-id="element-4034">
                    Live Chat
                  </span>
                </button>
                {/* Fixed tooltip positioning to ensure it's fully visible */}
                {showChatTooltip && <div className="fixed transform -translate-x-1/2 px-3 py-2 bg-gray-800 dark:bg-[#0A1C1A] text-white text-sm rounded shadow-lg z-50" style={{
              left: '50%',
              top: 'calc(50% + 40px)',
              maxWidth: '200px'
            }} data-id="element-4035">
                    Live chat is coming soon.
                  </div>}
              </div>
            </div>
          </div>
        </section>}
      {/* Feature Request Section */}
      {!showTicketHistory && !showFaqs && !showContactPreferences && <section className="border border-gray-200 dark:border-[#1A3330] rounded-lg overflow-hidden mt-8" data-id="element-4036">
          <div className="bg-gray-50 dark:bg-[#1A3330]/30 px-5 py-4 border-b border-gray-200 dark:border-[#1A3330]" data-id="element-4037">
            <div className="flex items-center" data-id="element-4038">
              <LightbulbIcon className="w-5 h-5 text-gray-500 dark:text-[#7A8F8A] mr-2" data-id="element-4039" />
              <h3 className="text-lg font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-4040">
                Request a Feature
              </h3>
            </div>
          </div>
          <div className="p-5" data-id="element-4041">
            {featureSubmitted ? <div className="p-4 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-md flex items-start" data-id="element-4042">
                <CheckCircleIcon className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" data-id="element-4043" />
                <div data-id="element-4044">
                  <p className="font-medium" data-id="element-4045">
                    Thanks! Your suggestion was received.
                  </p>
                  <p className="mt-1" data-id="element-4046">
                    We'll review your idea and consider it for future updates.
                  </p>
                </div>
              </div> : <form onSubmit={handleFeatureSubmit} data-id="element-4047">
                <div className="space-y-4" data-id="element-4048">
                  <div data-id="element-4049">
                    <label htmlFor="category" className="block text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-1" data-id="element-4050">
                      Category (Optional)
                    </label>
                    <select id="category" name="category" value={featureFormData.category} onChange={handleFeatureInputChange} className="w-full px-3 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent bg-white dark:bg-[#162825] text-gray-900 dark:text-[#F8FAF9] appearance-none" data-id="element-4051">
                      <option value="" data-id="element-4052">Select a category</option>
                      <option value="Store" data-id="element-4053">Store</option>
                      <option value="Dashboard" data-id="element-4054">Dashboard</option>
                      <option value="Analytics" data-id="element-4055">Analytics</option>
                      <option value="Products" data-id="element-4056">Products</option>
                      <option value="Payments" data-id="element-4057">Payments</option>
                      <option value="Security" data-id="element-4058">Security</option>
                      <option value="Other" data-id="element-4059">Other</option>
                    </select>
                  </div>
                  <div data-id="element-4060">
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-1" data-id="element-4061">
                      Describe your feature idea{' '}
                      <span className="text-red-500" data-id="element-4062">*</span>
                    </label>
                    <textarea id="description" name="description" value={featureFormData.description} onChange={handleFeatureInputChange} required rows={4} className="w-full px-3 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent bg-white dark:bg-[#162825] text-gray-900 dark:text-[#F8FAF9]" placeholder="I would like to see a feature that..." data-id="element-4063" />
                  </div>
                  <div className="flex justify-end" data-id="element-4064">
                    <button type="submit" disabled={isSubmittingFeature} className="px-4 py-2 bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-white font-medium rounded-lg hover:shadow-md transition-all duration-200 flex items-center" data-id="element-4065">
                      {isSubmittingFeature ? <>
                          <Loader2Icon className="w-4 h-4 mr-2 animate-spin" data-id="element-4066" />
                          Submitting...
                        </> : 'Submit Suggestion'}
                    </button>
                  </div>
                </div>
              </form>}
          </div>
        </section>}
      {/* Bug Report Section */}
      {!showTicketHistory && !showFaqs && !showContactPreferences && <section className="border border-gray-200 dark:border-[#1A3330] rounded-lg overflow-hidden mt-8" data-id="element-4067">
          <div className="bg-gray-50 dark:bg-[#1A3330]/30 px-5 py-4 border-b border-gray-200 dark:border-[#1A3330]" data-id="element-4068">
            <div className="flex items-center" data-id="element-4069">
              <BugIcon className="w-5 h-5 text-gray-500 dark:text-[#7A8F8A] mr-2" data-id="element-4070" />
              <h3 className="text-lg font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-4071">
                Report a Bug
              </h3>
            </div>
          </div>
          <div className="p-5" data-id="element-4072">
            {bugSubmitted ? <div className="p-4 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-md flex items-start" data-id="element-4073">
                <CheckCircleIcon className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" data-id="element-4074" />
                <div data-id="element-4075">
                  <p className="font-medium" data-id="element-4076">
                    Bug report submitted successfully!
                  </p>
                  <p className="mt-1" data-id="element-4077">
                    Our team will investigate the issue and work on a fix.
                  </p>
                </div>
              </div> : <form onSubmit={handleBugSubmit} data-id="element-4078">
                <div className="space-y-4" data-id="element-4079">
                  <div data-id="element-4080">
                    <label htmlFor="page" className="block text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-1" data-id="element-4081">
                      Page or area where the bug occurred{' '}
                      <span className="text-red-500" data-id="element-4082">*</span>
                    </label>
                    <input type="text" id="page" name="page" value={bugFormData.page} onChange={handleBugInputChange} required className="w-full px-3 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent bg-white dark:bg-[#162825] text-gray-900 dark:text-[#F8FAF9]" placeholder="e.g., Product Upload Page, Checkout, Dashboard" data-id="element-4083" />
                  </div>
                  <div data-id="element-4084">
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-1" data-id="element-4085">
                      Description of the issue{' '}
                      <span className="text-red-500" data-id="element-4086">*</span>
                    </label>
                    <textarea id="description" name="description" value={bugFormData.description} onChange={handleBugInputChange} required rows={4} className="w-full px-3 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent bg-white dark:bg-[#162825] text-gray-900 dark:text-[#F8FAF9]" placeholder="Please describe what happened and what you expected to happen..." data-id="element-4087" />
                  </div>
                  {/* Screenshot upload */}
                  <div data-id="element-4088">
                    <label className="block text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-1" data-id="element-4089">
                      Attach screenshot (Optional)
                    </label>
                    <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/*" className="hidden" data-id="element-4090" />
                    {bugScreenshot ? <div className="mt-2 relative" data-id="element-4091">
                        <div className="flex items-center p-3 bg-gray-50 dark:bg-[#1A3330] rounded-md border border-gray-200 dark:border-[#162825]" data-id="element-4092">
                          <div className="flex-shrink-0 mr-3" data-id="element-4093">
                            <ImageIcon className="w-5 h-5 text-gray-500 dark:text-[#7A8F8A]" data-id="element-4094" />
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-4095">
                            <p className="text-sm font-medium text-gray-900 dark:text-[#F8FAF9] truncate" data-id="element-4096">
                              {bugScreenshot.name}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-[#7A8F8A]" data-id="element-4097">
                              {(bugScreenshot.size / 1024).toFixed(1)} KB
                            </p>
                          </div>
                          <button type="button" onClick={handleRemoveScreenshot} className="ml-3 flex-shrink-0 text-gray-400 dark:text-[#7A8F8A] hover:text-gray-500 dark:hover:text-[#B0C1BD]" data-id="element-4098">
                            <XIcon className="w-4 h-4" data-id="element-4099" />
                          </button>
                        </div>
                      </div> : <button type="button" onClick={handleUploadClick} className="mt-1 px-4 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md text-sm font-medium text-gray-700 dark:text-[#B0C1BD] hover:bg-gray-50 dark:hover:bg-[#1A3330] transition-colors flex items-center" data-id="element-4100">
                        <ImageIcon className="w-4 h-4 mr-2" data-id="element-4101" />
                        Choose Image
                      </button>}
                  </div>
                  <div className="flex justify-end" data-id="element-4102">
                    <button type="submit" disabled={isSubmittingBug} className="px-4 py-2 bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-white font-medium rounded-lg hover:shadow-md transition-all duration-200 flex items-center" data-id="element-4103">
                      {isSubmittingBug ? <>
                          <Loader2Icon className="w-4 h-4 mr-2 animate-spin" data-id="element-4104" />
                          Submitting...
                        </> : 'Report Bug'}
                    </button>
                  </div>
                </div>
              </form>}
          </div>
        </section>}
      {/* Footer Links */}
      <div className="flex flex-wrap justify-center gap-6 pt-6 mt-8 border-t border-gray-200 dark:border-[#1A3330]" data-id="element-4105">
        <a href="/terms" className="text-sm text-gray-600 dark:text-[#B0C1BD] hover:text-[#2DB489] dark:hover:text-[#4ED1A0] flex items-center" data-id="element-4106">
          <FileTextIcon className="w-4 h-4 mr-1.5" data-id="element-4107" />
          Terms & Privacy
        </a>
      </div>
    </div>;
};